import * as React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { Icon } from "./Icon";

type Size = "compact" | "default" | "wide";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Omit for a headerless modal — only a floating close button is rendered. */
  title?: string;
  eyebrow?: string;
  lede?: string;
  size?: Size;
  footer?: React.ReactNode;
  footerLayout?: "split" | "full";
  children: React.ReactNode;
  /**
   * Portal target. Pass the body-appended host so the overlay escapes drei's
   * <Scroll html> transform parent (where position:fixed breaks). Falls back
   * to document.body.
   */
  container?: HTMLElement | null;
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

const MAX_W: Record<Size, string> = {
  compact: "max-w-[440px]",
  default: "max-w-[520px]",
  wide: "max-w-[720px]",
};

/* The mobile sheet is edge-to-edge, so its inner gutter *is* the page gutter —
   it has to match `.t-container` (24px = --space-6) or the modal copy sits on a
   different left edge than the nav behind it. Desktop keeps the roomier 40px. */
const GUTTER_MOBILE = "px-6";
const GUTTER_DESKTOP = "px-10";

export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  lede,
  size = "default",
  footer,
  footerLayout = "split",
  children,
  container,
}: Props) {
  const { t } = useTranslation('ui');
  const isMobile = useBreakpoint() === "mobile";
  const gutter = isMobile ? GUTTER_MOBILE : GUTTER_DESKTOP;
  const panelRef = React.useRef<HTMLDivElement>(null);
  // -Infinity, not 0: "no input yet" must always fail the "was that recent?"
  // check below, regardless of how much wall-clock time performance.now()
  // itself has accumulated (near-zero early in a fast test run/process boot).
  const lastInputAtRef = React.useRef(-Infinity);
  const titleId = React.useId();
  const [mounted, setMounted] = React.useState(open);
  const [entered, setEntered] = React.useState(false);

  const [dragY, setDragY] = React.useState(0);
  const dragStartRef = React.useRef<number | null>(null);

  const onDragStart = (e: React.PointerEvent) => {
    if (!isMobile) return;
    dragStartRef.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (dragStartRef.current === null) return;
    setDragY(Math.max(0, e.clientY - dragStartRef.current)); // down-only
  };
  const onDragEnd = () => {
    if (dragStartRef.current === null) return;
    const shouldClose = dragY > 120;
    dragStartRef.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  };

  React.useEffect(() => {
    if (open) setDragY(0);
  }, [open]);

  React.useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  React.useEffect(() => {
    if (!mounted) return;
    if (open) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const fallback = setTimeout(() => setMounted(false), 260);
    return () => clearTimeout(fallback);
  }, [open, mounted]);

  React.useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      // iOS Safari pans the WINDOW (not #root) to reveal the keyboard over the
      // fixed sheet and leaves it panned. The app only ever scrolls #root (or
      // the drei container) — body clips at one viewport, so a leftover window
      // scroll renders as a blank white page. Any window scroll is bogus here.
      if (window.scrollY) window.scrollTo(0, 0);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  // iOS Safari contact AutoFill scrolls the focused field's nearest scrollable
  // ancestor to its very end — device-verified WebKit bug, independent of
  // autocomplete markup. That scroller may be the body div OR a nested scroll
  // region inside the form (ReviewForm's fields area); scroll events don't
  // bubble, so listen in capture on the whole panel and correct whichever
  // scroller fired. Right after an autofill-signature input burst, put the
  // scroll back so the active field stays in view.
  React.useEffect(() => {
    if (!open || !mounted) return;
    const panel = panelRef.current;
    if (!panel) return;
    const onInput = () => {
      lastInputAtRef.current = performance.now();
    };
    const onScroll = (e: Event) => {
      if (performance.now() - lastInputAtRef.current > 500) return;
      const scroller = e.target;
      if (!(scroller instanceof HTMLElement)) return;
      const active = document.activeElement;
      if (
        !(active instanceof HTMLElement) ||
        !scroller.contains(active) ||
        !active.matches("input,textarea,select")
      )
        return;
      const scrollerRect = scroller.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      if (
        activeRect.bottom < scrollerRect.top + 8 ||
        activeRect.top > scrollerRect.bottom - 8
      ) {
        scroller.scrollTop += activeRect.top - scrollerRect.top - 24;
      }
    };
    panel.addEventListener("input", onInput, true);
    panel.addEventListener("scroll", onScroll, true);
    return () => {
      panel.removeEventListener("input", onInput, true);
      panel.removeEventListener("scroll", onScroll, true);
    };
  }, [open, mounted]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex justify-center ${
        isMobile ? "items-end p-0" : "items-center p-6"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : t('dialog')}
    >
      <div
        className={`absolute inset-0 bg-primary/55 transition-opacity duration-200 ease-out ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        onTransitionEnd={(e) => {
          if (!open && e.target === panelRef.current) setMounted(false);
        }}
        style={
          isMobile && dragStartRef.current !== null
            ? { transform: `translateY(${dragY}px)`, transition: "none" }
            : undefined
        }
        className={`relative flex w-full flex-col overflow-hidden border border-primary/15 bg-white shadow-[0_2px_4px_rgba(54,98,142,0.10),0_32px_64px_-20px_rgba(20,40,65,0.45)] transition duration-200 ease-out motion-reduce:transition-none ${
          isMobile
            ? `max-w-none max-h-[88vh] rounded-t-2xl pb-[env(safe-area-inset-bottom)] ${
                entered ? "translate-y-0" : "translate-y-full"
              }`
            : `${MAX_W[size]} max-h-full ${
                entered
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 translate-y-2 scale-[0.98]"
              }`
        }`}
      >
        {isMobile ? (
          <div
            className="flex flex-none cursor-grab touch-none justify-center pt-3 pb-3 active:cursor-grabbing"
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
            role="button"
            aria-label={t("close")}
            tabIndex={-1}
          >
            <span className="block h-1.5 w-10 rounded-full bg-primary/25" />
          </div>
        ) : null}
        {title ? (
          <div
            className={`flex flex-none items-start justify-between gap-6 border-b border-primary/15 pb-6 pt-9 ${gutter}`}
          >
            <div className="min-w-0 flex-1">
              {eyebrow ? (
                <div className="mb-4 flex items-center gap-3 text-micro font-medium uppercase tracking-eyebrow text-primary/50">
                  <span
                    aria-hidden="true"
                    className="block h-2.5 w-2.5 flex-none rotate-45 border border-primary/50"
                  />
                  <span>{eyebrow}</span>
                  <span className="h-px max-w-[80px] flex-1 bg-primary/15" />
                </div>
              ) : null}
              <h2
                id={titleId}
                className="t-section text-primary [text-wrap:balance]"
              >
                {title}
              </h2>
              {lede ? (
                <p className="mt-3.5 max-w-[42ch] text-sm leading-snug text-primary/80 [text-wrap:pretty]">
                  {lede}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('close')}
              className="-mr-2 -mt-1.5 flex h-9 w-9 flex-none items-center justify-center text-[18px] text-primary transition-opacity duration-200 hover:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <Icon name="close" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center text-[18px] text-primary transition-opacity duration-200 hover:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <Icon name="close" />
          </button>
        )}

        <div
          className={`flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-8 pt-7 ${gutter}`}
        >
          {children}
        </div>

        {footer ? (
          <div
            className={`flex-none border-t border-primary/15 ${gutter} ${
              footerLayout === "full"
                ? "flex py-7"
                : "flex items-center justify-between gap-4 pb-9 pt-6"
            }`}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    container ?? document.body,
  );
}
